import {
  ReactElement,
  ReactNode,
  useEffect,
  createContext,
  useContext,
  useSyncExternalStore,
  useRef,
  useMemo,
} from 'react';
import type { Channel } from './Channel';
import { RealtimeClient } from './RealtimeClient';
import { useUser } from 'services/session';
import { auth } from 'services/auth';
import type { ChannelState } from './types';
import type { ChannelError, ChannelEvent, PresenceUser } from './protocol';
import { fetcher } from 'services/api';

const ChannelContext = createContext<Channel | null>(null);

export function ChannelProvider({ channel, children }: { channel: Channel; children: ReactNode }): ReactElement {
  return <ChannelContext.Provider value={channel}>{children}</ChannelContext.Provider>;
}

export function useChannel(): Channel {
  const ctx = useContext(ChannelContext);
  if (!ctx) throw new Error('useChannel must be used within a ChannelProvider');
  return ctx;
}

export const RealtimeContext = createContext<RealtimeClient | null>(null);

interface RealtimeProviderProps {
  children: ReactNode;
}

const realtimeClient = new RealtimeClient({
  onError(error) {
    if (error.payload.code === 'TOKEN_EXPIRED') {
      auth.fetchSession();
    }
  },
  onTokenStale(signal) {
    // Refetch the session to mint a fresh connection token; the new `realtime.token`
    // re-runs the provider effect and reconnects. Returned so the client awaits it;
    // `signal` aborts the fetch if a newer connect/teardown supersedes this refresh.
    return auth.fetchSession(signal);
  },
  getChannelToken: async (channel) => {
    const res = await fetcher<{ token: string }>({
      method: 'GET',
      url: `realtime/token/${channel}/`,
    });
    return res.token;
  },
});

if (import.meta.env.DEV) (window as any).__rt = realtimeClient;

export function RealtimeProvider({ children }: RealtimeProviderProps): ReactElement {
  const { realtime } = useUser();

  useEffect(() => {
    if (!realtime) {
      realtimeClient.disconnect();
      return;
    }

    realtimeClient.connect(realtime);

    return () => {
      realtimeClient.disconnect();
    };
  }, [realtime]);

  return <RealtimeContext.Provider value={realtimeClient}>{children}</RealtimeContext.Provider>;
}

export function useRealtimeClient(): RealtimeClient {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtimeClient must be used within a RealtimeProvider');
  return ctx;
}

export function useRealtimeConnection() {
  const client = useRealtimeClient();
  return useSyncExternalStore(client.subscribe, client.getSnapshot);
}

/** Our own connection id while connected, else `null` (e.g. to exclude our own presence). */
export function useMyConnectionId(): string | null {
  const connection = useRealtimeConnection();
  return connection.status === 'connected' ? connection.user.connection_id : null;
}

export function useChannelSubscription(name: string, onError?: (error: ChannelError) => void): Channel {
  const client = useRealtimeClient();

  const cbRef = useRef(onError);
  useEffect(() => {
    cbRef.current = onError;
  });

  const channel = useMemo(() => {
    return client.channel(name, {
      onError: (err) => cbRef.current?.(err),
    });
  }, [client, name]);

  useEffect(() => {
    channel.subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [channel]);

  return channel;
}

export function useChannelListener(
  channel: Channel,
  listener: (message: ChannelEvent<any>) => boolean | void,
  priority = 0,
): void {
  useEffect(() => {
    return channel.addMessageListener(listener, priority);
  }, [channel, listener, priority]);
}

export function useChannelState(channel: Channel): { presence: PresenceUser[]; state: ChannelState } {
  return useSyncExternalStore(channel.subscribeToChannel, channel.getChannelSnapshot);
}
